// Acquisition-channel attribution: capture ?source=<slug> off a tracking link (generated
// in the admin panel), persist it across the pre-auth signup funnel, and stamp it onto the
// new business at registration. Pure localStorage/sessionStorage helper - no Supabase calls
// here, per the MVC rule that only model.js talks to Supabase (see logRegistrationEvent there).
//
// Latest-channel attribution (2026-10-12, was first-touch): a new ?source= replaces the
// stored one, so a 9-day-old button click can't steal credit from today's outreach email.
// One exception: a marketing-site button slug (site-*) never replaces a real channel
// source - the site already forwards the channel when it knows it, so a site-* arriving
// on top of a stored channel means the visitor came back later without a tracking link.
//
// Backup copy: pointbunny.com also writes the source to a pb_source cookie on
// .pointbunny.com (shared with this subdomain), so the source survives even if the
// ?source= never makes it through to this page's URL.

const VISITOR_KEY = "pointbunny_visitor_id";
const SOURCE_KEY = "pointbunny_source"; // JSON: { source, capturedAt }
const SOURCE_COOKIE = "pb_source"; // set by the marketing site, Domain=.pointbunny.com
const SOURCE_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const FIRED_PREFIX = "pointbunny_src_evt_"; // sessionStorage, one flag per funnel stage

const isExpired = (capturedAt) => !capturedAt || Date.now() - capturedAt > SOURCE_TTL_MS;
const isButtonSlug = (source) => /^site-/.test(source);

const readSourceCookie = function () {
  try {
    const match = document.cookie.match(/(?:^|;\s*)pb_source=([^;]*)/);
    return match ? decodeURIComponent(match[1]) || null : null;
  } catch (_) {
    return null;
  }
};

const clearSourceCookie = function () {
  try {
    const domain = window.location.hostname.endsWith("pointbunny.com") ? "; domain=.pointbunny.com" : "";
    document.cookie = `${SOURCE_COOKIE}=; max-age=0; path=/${domain}`;
  } catch (_) {
    // best-effort
  }
};

// Long-lived anonymous id grouping this visitor's funnel events together.
export const getOrCreateVisitorId = function () {
  try {
    let id = localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch (_) {
    return null;
  }
};

export const getStoredSource = function () {
  try {
    const raw = localStorage.getItem(SOURCE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (isExpired(parsed.capturedAt)) {
      localStorage.removeItem(SOURCE_KEY);
      return null;
    }
    return parsed;
  } catch (_) {
    return null;
  }
};

// Called once the source is stamped on the new business, so later visits aren't
// counted again.
export const clearStoredSource = function () {
  try {
    localStorage.removeItem(SOURCE_KEY);
  } catch (_) {
    // best-effort
  }
  clearSourceCookie();
};

const storeSource = function (source) {
  const existing = getStoredSource();
  if (existing?.source === source) return;
  if (existing && isButtonSlug(source) && !isButtonSlug(existing.source)) return;
  localStorage.setItem(SOURCE_KEY, JSON.stringify({ source, capturedAt: Date.now() }));
};

// Reads ?source= off the CURRENT page URL (falling back to the marketing site's pb_source
// cookie) and stores it. Must run before anything strips the query string. Returns the raw
// slug found in this page's URL, or null - the caller uses this to know whether THIS load
// is the one that should fire link_opened / auto-open signup. A cookie-only source is
// stored but returns null: it would otherwise push a returning "Log in" visitor to sign-up.
export const captureSourceFromUrl = function () {
  try {
    const urlSource = new URLSearchParams(window.location.search).get("source");
    const source = urlSource || readSourceCookie();
    if (source) storeSource(source);
    return urlSource;
  } catch (_) {
    return null;
  }
};

export const hasFiredEvent = function (type) {
  try {
    return sessionStorage.getItem(FIRED_PREFIX + type) === "1";
  } catch (_) {
    return false;
  }
};

export const markEventFired = function (type) {
  try {
    sessionStorage.setItem(FIRED_PREFIX + type, "1");
  } catch (_) {
    // best-effort
  }
};
